import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import { Calendar, MapPin, User, X } from "lucide-react";
import React, { useState } from "react";
import { useToast } from "../../../contexts/ToastContext";

interface ScheduleViewingModalProps {
	viewing?: any;
	properties?: any[];
	agents?: any[];
	onClose: () => void;
	onSave: (data: any) => void;
}

const ScheduleViewingModal = ({
	viewing,
	properties = [],
	agents = [],
	onClose,
	onSave,
}: ScheduleViewingModalProps) => {
	const { error: showError } = useToast();

	// Extract date and time from viewing_date if it's a datetime string
	const getDateFromViewing = () => {
		if (!viewing?.viewing_date) return "";
		const date = new Date(viewing.viewing_date);
		return date.toISOString().split("T")[0];
	};

	const getTimeFromViewing = () => {
		if (!viewing?.viewing_date) return "";
		const date = new Date(viewing.viewing_date);
		const hours = date.getHours().toString().padStart(2, "0");
		const minutes = date.getMinutes().toString().padStart(2, "0");
		return `${hours}:${minutes}`;
	};

	const [formData, setFormData] = useState({
		property: viewing?.property || "",
		property_name: viewing?.property_name || "",
		property_address: viewing?.property_address || "",
		buyer_name: viewing?.buyer_name || "",
		buyer_email: viewing?.buyer_email || "",
		buyer_phone: viewing?.buyer_phone || "",
		date: getDateFromViewing(),
		time: getTimeFromViewing(),
		duration: viewing?.duration?.toString() || "30",
		notes: viewing?.notes || "",
		agent: viewing?.agent || "",
		status: viewing?.status || "scheduled",
	});

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		if (!formData.property) {
			showError("Please select a property.");
			return;
		}

		if (!formData.buyer_name?.trim()) {
			showError("Buyer name is required.");
			return;
		}

		if (!formData.date) {
			showError("Please select a date for the viewing.");
			return;
		}

		if (!formData.time) {
			showError("Please select a time for the viewing.");
			return;
		}

		// Validate email format if provided
		if (formData.buyer_email) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(formData.buyer_email)) {
				showError("Please enter a valid email address.");
				return;
			}
		}

		const viewingData = {
			...formData,
			duration: parseInt(formData.duration),
		};

		onSave(viewingData);
	};

	// Generate time options (9 AM to 6 PM in 30-minute intervals)
	const generateTimeOptions = () => {
		const options = [];
		for (let hour = 9; hour <= 18; hour++) {
			for (let minute = 0; minute < 60; minute += 30) {
				const time = `${hour?.toString()?.padStart(2, "0")}:${minute
					?.toString()
					?.padStart(2, "0")}`;
				const displayTime = new Date(`2000-01-01T${time}`)?.toLocaleTimeString(
					"en-US",
					{
						hour: "numeric",
						minute: "2-digit",
						hour12: true,
					},
				);
				options?.push({ value: time, label: displayTime });
			}
		}
		return options;
	};

	const durationOptions = [
		{ value: "15", label: "15 minutes" },
		{ value: "30", label: "30 minutes" },
		{ value: "45", label: "45 minutes" },
		{ value: "60", label: "1 hour" },
		{ value: "90", label: "1.5 hours" },
		{ value: "120", label: "2 hours" },
	];

	const statusOptions = [
		{ value: "scheduled", label: "Scheduled" },
		{ value: "confirmed", label: "Confirmed" },
		{ value: "completed", label: "Completed" },
		{ value: "cancelled", label: "Cancelled" },
	];

	// Get minimum date (today)
	const today = new Date()?.toISOString()?.split("T")?.[0];

	return (
		<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
			<div className="bg-card rounded-lg border border-border w-full max-w-2xl my-8">
				<div className="flex items-center justify-between p-6 border-b border-border">
					<h2 className="text-xl font-semibold text-foreground">
						{viewing ? "Edit Viewing" : "Schedule New Viewing"}
					</h2>
					<Button variant="ghost" size="icon" onClick={onClose}>
						<X className="w-5 h-5" />
					</Button>
				</div>

				<form onSubmit={handleSubmit} className="p-6 space-y-6">
					{/* Property Information */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground flex items-center gap-2">
							<MapPin className="w-5 h-5" />
							Property Information
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Property *
								</label>
								<Select
									value={formData.property}
									onValueChange={(value) => {
										const selectedProperty = properties.find(
											(p) => p.id === value,
										);
										handleInputChange("property", value as string);
										if (selectedProperty) {
											handleInputChange("property_name", selectedProperty.name);
											handleInputChange(
												"property_address",
												selectedProperty.address || "",
											);
										}
									}}
									options={properties.map((p) => ({
										value: p.id,
										label: `${p.name} - ${p.address || ""}`,
									}))}
									placeholder="Select a property"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Property Address
								</label>
								<Input
									value={formData.property_address}
									onChange={(e) =>
										handleInputChange("property_address", e.target.value)
									}
									placeholder="123 Main St, City, State 12345"
									readOnly
								/>
							</div>
						</div>
					</div>

					{/* Buyer Information */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground flex items-center gap-2">
							<User className="w-5 h-5" />
							Buyer Information
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Buyer Name *
								</label>
								<Input
									value={formData?.buyer_name}
									onChange={(e) =>
										handleInputChange("buyer_name", e?.target?.value)
									}
									placeholder="John Smith"
									required
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Email
								</label>
								<Input
									type="email"
									value={formData?.buyer_email}
									onChange={(e) =>
										handleInputChange("buyer_email", e?.target?.value)
									}
									placeholder="john@example.com"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Phone
								</label>
								<Input
									type="tel"
									value={formData?.buyer_phone}
									onChange={(e) =>
										handleInputChange("buyer_phone", e?.target?.value)
									}
									placeholder="(555) 123-4567"
								/>
							</div>
						</div>
					</div>

					{/* Viewing Details */}
					<div className="space-y-4">
						<h3 className="text-lg font-medium text-foreground flex items-center gap-2">
							<Calendar className="w-5 h-5" />
							Viewing Details
						</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Date *
								</label>
								<Input
									type="date"
									value={formData?.date}
									onChange={(e) => handleInputChange("date", e?.target?.value)}
									min={today}
									required
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Time *
								</label>
								<Select
									value={formData?.time}
									onValueChange={(value) =>
										handleInputChange("time", value as string)
									}
									options={generateTimeOptions()}
									placeholder="Select time"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Duration
								</label>
								<Select
									value={formData?.duration}
									onValueChange={(value) =>
										handleInputChange("duration", value as string)
									}
									options={durationOptions}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Status
								</label>
								<Select
									value={formData?.status}
									onValueChange={(value) =>
										handleInputChange("status", value as string)
									}
									options={statusOptions}
								/>
							</div>

							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-foreground mb-2">
									Assign Agent (Optional)
								</label>
								<Select
									value={formData?.agent}
									onValueChange={(value) =>
										handleInputChange("agent", value as string)
									}
									options={[
										{ value: "", label: "No agent assigned" },
										...(agents.map((agent) => ({
											value: agent.id,
											label: `${agent.first_name} ${agent.last_name} (${agent.email})`,
										})) || []),
									]}
									placeholder="Select an agent"
								/>
							</div>
						</div>
					</div>

					{/* Notes */}
					<div>
						<label className="block text-sm font-medium text-foreground mb-2">
							Notes
						</label>
						<textarea
							value={formData?.notes}
							onChange={(e) => handleInputChange("notes", e?.target?.value)}
							rows={3}
							className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
							placeholder="Special instructions, buyer preferences, etc..."
						/>
					</div>

					{/* Form Actions */}
					<div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
						<Button type="button" variant="outline" size="sm" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" size="sm">
							{viewing ? "Update" : "Schedule"}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ScheduleViewingModal;
